import { useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface CreateListeModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const presetColorValues = [
  { value: '#7c3aed', key: 'violet' },
  { value: '#3b82f6', key: 'blue' },
  { value: '#10b981', key: 'green' },
  { value: '#f59e0b', key: 'amber' },
  { value: '#ef4444', key: 'red' },
  { value: '#ec4899', key: 'pink' },
  { value: '#06b6d4', key: 'cyan' },
  { value: '#8b5cf6', key: 'indigo' },
];

const presetIconValues = [
  { value: 'list', key: 'list' },
  { value: 'star', key: 'star' },
  { value: 'heart', key: 'heart' },
  { value: 'flag', key: 'flag' },
  { value: 'folder', key: 'folder' },
  { value: 'briefcase', key: 'briefcase' },
  { value: 'home', key: 'home' },
  { value: 'zap', key: 'zap' },
];

export function CreateListeModal({ onClose, onCreated }: CreateListeModalProps) {
  const { t } = useTranslation('zillotasks');
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [couleur, setCouleur] = useState('#7c3aed');
  const [icone, setIcone] = useState('list');
  const [submitting, setSubmitting] = useState(false);

  const presetColors = presetColorValues.map((c) => ({ ...c, label: t(`createList.colors.${c.key}`) }));
  const presetIcons = presetIconValues.map((ic) => ({ ...ic, label: t(`createList.icons.${ic.key}`) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nom.trim()) return;

    setSubmitting(true);
    try {
      const res = await api.createZtListe({
        nom: nom.trim(),
        description: description.trim() || undefined,
        couleur,
        icone,
      });

      if (res.success) {
        toast.success(t('createList.createSuccess'));
        onCreated();
        onClose();
      } else {
        toast.error(res.error?.message || t('createList.createError'));
      }
    } catch (error) {
      toast.error(t('createList.createListError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('createList.title')}</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('createList.fields.name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder={t('createList.fields.namePlaceholder')}
              autoFocus
              required
              maxLength={100}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('createList.fields.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('createList.fields.descriptionPlaceholder')}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Icone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createList.fields.icon')}
            </label>
            <div className="flex flex-wrap gap-2">
              {presetIcons.map((ic) => (
                <button
                  key={ic.value}
                  type="button"
                  onClick={() => setIcone(ic.value)}
                  title={ic.label}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    icone === ic.value
                      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {ic.label}
                </button>
              ))}
            </div>
          </div>

          {/* Couleur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('createList.fields.color')}
            </label>
            <div className="flex flex-wrap gap-2">
              {presetColors.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCouleur(c.value)}
                  title={c.label}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    couleur === c.value
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.value }}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: couleur }}
            />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {nom || t('createList.preview')}
            </span>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            {t('createList.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!nom.trim() || submitting}
            className="px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? t('createList.submitting') : t('createList.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateListeModal;
